import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { words, upperFirst, isNil, isEmpty } from 'lodash';
import { List } from 'office-ui-fabric-react/lib/List';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import Link from '../Link';
import styles from './FilterDetails.style';
import {
  dimensionListTypes,
  getCalcParamsText,
  computedValueTypes,
  operatorMap,
} from '../../constants';
import { getFilterDisplayName } from '../../utils/filterDisplayName';
import { formatDateString } from '../../utils';
import { calculationMethods } from '../CalculationFeatures/constants';

export function includeExcludePrefix() {
  return filter => {
    const { type, key } = filter;
    const capitalAndPluralizedType = `${type.charAt(0).toUpperCase() + type.slice(1)}s`;
    return (
      <>
        <strong key={key}>{capitalAndPluralizedType}</strong> transactions
      </>
    );
  };
}

const getParams = assignment => {
  if (Array.isArray(assignment.calcParams)) {
    return assignment.calcParams;
  }
  return Object.keys(assignment.calcParams).map(key => ({
    property: key,
    value: assignment.calcParams[key],
  }));
};

export function getPrefixSentence(calculation, rateAssignment) {
  return (
    <>
      {(calculation.slug === 'greater-revenue-and-price' ||
        calculation.slug === 'greater-revenue-or-unit-rate') && <strong>Greater of </strong>}

      {rateAssignment.calcParams &&
        getParams(rateAssignment).map((calcParam, index) => {
          const { value, property } = calcParam;
          const isLast = index === rateAssignment.calcParams.length - 1;
          let text;

          if (property === 'unitRate') {
            text = `${getCalcParamsText(property)} of ${value}`;
          } else {
            text = `${value}${getCalcParamsText(property)}`;
          }

          return (
            <span key={property}>
              <strong>{text}</strong>
              {!isLast && ' or '}
            </span>
          );
        })}
    </>
  );
}

export function calculationPrefix(calculation) {
  return filter => {
    return getPrefixSentence(calculation, filter);
  };
}

function formatTierNumber(tiering) {
  if (isNil(tiering)) {
    return 'MAX';
  }

  return tiering.toLocaleString();
}

export const calculationFeaturePrefix = method => filter => {
  const { fields, detailName } = calculationMethods[method];

  if (isEmpty(fields)) {
    if (detailName) {
      return <strong>{detailName}</strong>;
    }
    return null;
  }

  const sentence = fields.map((field, idx) => {
    const isLast = idx === fields.length - 1;
    const value = filter.calcParams[field.key];
    const text = `${!isNil(value) ? value : ''}${field.detailName || field.name}`;
    return (
      <span>
        <strong>{text}</strong>
        {!isLast && ' and '}
      </span>
    );
  });
  return <>{sentence}</>;
};

const FilterDetails = ({ filters, prefix }) => {
  if (
    !filters ||
    filters.length === 0 ||
    filters.filter(filter => filter && filter._id).length === 0
  ) {
    return (
      <div className={styles.filterItem}>
        <FontIcon className={styles.infoIcon} iconName="Info" arianame="Info" />
        No filters currently added to this contract. Select the Add a Filter button below to create
        a new filter.
      </div>
    );
  }

  const getPropertyText = property => {
    const dimensionListType = dimensionListTypes.find(e => e.key === property);

    if (dimensionListType) {
      return dimensionListType.text;
    }

    const computedValueType = computedValueTypes.find(e => e.key === property);

    if (computedValueType) {
      return computedValueType.text;
    }

    return words(property)
      .map(word => upperFirst(word))
      .join(' ');
  };

  const buildDimensionSectionForFilter = dimensions => {
    if (dimensions && dimensions.length > 0) {
      return dimensions.map(dimension => {
        const { property, propertyType } = dimension;
        let dimensionPrefix;

        // Dimension prefix
        if (property.includes('channel')) {
          dimensionPrefix = 'via';
        } else if (property.includes('geography') || property.includes('advance-group')) {
          dimensionPrefix = 'in';
        } else {
          dimensionPrefix = 'with';
        }

        // Dimension property
        const dimensionProperty =
          propertyType === 'dimensionList'
            ? `${getPropertyText(property)} List`
            : getPropertyText(property);

        // Dimension operator
        const dimensionOperator = dimension.operator ? operatorMap.get(dimension.operator) : '';

        // Dimension value
        const dimensionValue = getFilterDisplayName(dimension);

        // Dimension link
        const dimensionLink =
          propertyType === 'dimensionList'
            ? `/dimensionLists/${dimension.value}/${dimension.property}`
            : property === 'product' && `/products/${dimension.value}`;

        return {
          dimensionPrefix,
          dimensionProperty,
          dimensionOperator,
          dimensionValue,
          dimensionLink,
        };
      });
    }
    return [];
  };

  const getDimensionSection = dimensions => {
    const dimensionSections = buildDimensionSectionForFilter(dimensions);
    return dimensionSections.map(dimensionSection => (
      <React.Fragment key={dimensionSection.dimensionProperty}>
        {' '}
        {dimensionSection.dimensionPrefix} {dimensionSection.dimensionProperty}{' '}
        {dimensionSection.dimensionOperator}
        {dimensionSection?.dimensionLink && (
          <Link
            className={styles.filterDimension}
            href={dimensionSection.dimensionLink}
            target="_blank"
          >
            <strong> {dimensionSection.dimensionValue}</strong>
          </Link>
        )}
        {!dimensionSection?.dimensionLink && <strong> {dimensionSection.dimensionValue}</strong>}
      </React.Fragment>
    ));
  };

  const getDateSection = (effectiveDate, terminationDate) => {
    const fEffective = formatDateString(effectiveDate);
    const fTermination = formatDateString(terminationDate);
    return (
      <>
        {' '}
        from <strong>{fEffective}</strong> to <strong>{fTermination}</strong>
      </>
    );
  };

  const getTieringSection = useCallback((tierId, tieringUpper, tieringMethod) => {
    let unitString;
    switch (tieringMethod) {
      case 'netRevenue':
      case 'adjustedGrossRevenue':
        unitString = 'revenue';
        break;
      case 'netRevenueUnits':
      case 'adjustedGrossUnits':
        unitString = 'units';
        break;
      default:
        throw new Error(`Unknown tiering method: ${tieringMethod}`);
    }

    return (
      <span key={tierId}>
        {' '}
        for transactions in{' '}
        <strong>
          Tier {tierId} (to {formatTierNumber(tieringUpper)} {unitString})
        </strong>
      </span>
    );
  }, []);

  const FilterItem = item => {
    const { effectiveDate, terminationDate, dimensions, key, tierId, tieringUpper, tieringMethod } =
      item;

    return (
      <div className={styles.filterItem} key={key}>
        {prefix(item)}
        {!isNil(tierId) && getTieringSection(tierId, tieringUpper, tieringMethod)}
        {getDimensionSection(dimensions)}
        {getDateSection(effectiveDate, terminationDate)}
      </div>
    );
  };

  return <List getKey={filter => filter._id} items={filters} onRenderCell={FilterItem} />;
};

FilterDetails.propTypes = {
  prefix: PropTypes.func,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      type: PropTypes.string,
      effectiveDate: PropTypes.string,
      terminationDate: PropTypes.string,
      dimensions: PropTypes.arrayOf(
        PropTypes.shape({
          property: PropTypes.string,
          value: PropTypes.string,
          operator: PropTypes.string,
        }),
      ),
    }),
  ),
};

FilterDetails.defaultProps = {
  filters: [],
  prefix: includeExcludePrefix(),
};

export default FilterDetails;
